/** Calendar days as YYYY-MM-DD in local time — the planner never needs hours. */

export function iso(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function parse(isoDay: string): Date {
  const [y, m, d] = isoDay.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

export const today = (): string => iso(new Date())

export function addDays(isoDay: string, days: number): string {
  const d = parse(isoDay)
  d.setDate(d.getDate() + days)
  return iso(d)
}

/** Monday of the week containing the day. */
export function weekStart(isoDay: string): string {
  const d = parse(isoDay)
  const offset = (d.getDay() + 6) % 7
  return addDays(isoDay, -offset)
}

export function monthStart(isoDay: string): string {
  return `${isoDay.slice(0, 7)}-01`
}

export function addMonths(isoDay: string, months: number): string {
  const d = parse(monthStart(isoDay))
  d.setMonth(d.getMonth() + months)
  return iso(d)
}

export function monthEnd(isoDay: string): string {
  return addDays(addMonths(isoDay, 1), -1)
}

const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'short' })
const dayMonth = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' })
const longDay = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })
const monthYear = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' })

/** "Mi 29.10." */
export function short(isoDay: string): string {
  const d = parse(isoDay)
  // de-DE formats "29.10." itself — no extra dot.
  const dm = dayMonth.format(d)
  return `${weekday.format(d).replace('.', '')} ${dm.endsWith('.') ? dm : `${dm}.`}`
}

/** "Heute", "Morgen", "Gestern" or "Mi 29.10." */
export function relative(isoDay: string): string {
  const t = today()
  if (isoDay === t) return 'Heute'
  if (isoDay === addDays(t, 1)) return 'Morgen'
  if (isoDay === addDays(t, -1)) return 'Gestern'
  return short(isoDay)
}

export const long = (isoDay: string): string => longDay.format(parse(isoDay))
export const month = (isoDay: string): string => monthYear.format(parse(isoDay))
